import * as THREE from 'three';
import { box, cyl, M, mat, mesh, sphere, texMat } from '../materials';
import {
  americanFlagTexture,
  cardboardTexture,
  poolWallTexture,
  signTexture,
  waterTexture,
} from '../textures';
import { flagpole, waveFlag } from './props';

export interface BuiltDecor {
  group: THREE.Group;
  /** Optional per-frame animation. */
  update?: (time: number, dt: number, night: number) => void;
}

function tube(points: Array<[number, number, number]>, radius: number, material: THREE.Material): THREE.Mesh {
  const curve = new THREE.CatmullRomCurve3(points.map(([x, y, z]) => new THREE.Vector3(x, y, z)));
  return mesh(new THREE.TubeGeometry(curve, 10, radius, 6, false), material);
}

export function flamingo(): THREE.Group {
  const g = new THREE.Group();
  const pink = mat('#ff6fa8', { rough: 0.35 });
  const b = sphere(0.18, pink, 0, 0.78, 0, 12);
  b.scale.set(1.5, 0.85, 0.85);
  g.add(b);
  g.add(
    tube(
      [
        [0.2, 0.82, 0],
        [0.32, 1.0, 0],
        [0.12, 1.12, 0],
        [0.18, 1.3, 0],
      ],
      0.035,
      pink,
    ),
  );
  g.add(sphere(0.07, pink, 0.2, 1.32, 0, 10));
  const beak = mesh(new THREE.ConeGeometry(0.03, 0.12, 6), M.black, 0.28, 1.28, 0);
  beak.rotation.z = -2.2;
  g.add(beak);
  for (const z of [-0.05, 0.05]) g.add(cyl(0.01, 0.01, 0.72, M.steel, 0, 0.36, z, 4));
  return g;
}

export function gnome(hatColor: string): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.12, 0.16, 0.3, mat('#2f5fa8'), 0, 0.15, 0, 10));
  g.add(sphere(0.1, mat('#f1c6a8'), 0, 0.38, 0, 10));
  const beard = mesh(new THREE.ConeGeometry(0.1, 0.22, 8), mat('#f4f4f4'), 0, 0.27, 0.05);
  beard.rotation.x = Math.PI;
  g.add(beard);
  g.add(mesh(new THREE.ConeGeometry(0.11, 0.3, 8), mat(hatColor), 0, 0.58, 0));
  return g;
}

const GOOSE_OUTFITS = ['#f2c200', '#c0392b', '#5e6b3a', '#ff7eb6', '#2b6cb0', '#ffffff'];

export function porchGoose(): BuiltDecor & { setOutfit: (i: number) => void } {
  const g = new THREE.Group();
  const stone = mat('#e3e0d8', { rough: 1 });
  const body = sphere(0.2, stone, 0, 0.24, 0, 12);
  body.scale.set(1.35, 0.9, 0.9);
  g.add(body);
  g.add(cyl(0.05, 0.06, 0.3, stone, 0.18, 0.45, 0, 8));
  g.add(sphere(0.08, stone, 0.2, 0.62, 0, 10));
  const beak = mesh(new THREE.ConeGeometry(0.035, 0.12, 6), mat('#f08a24'), 0.31, 0.6, 0);
  beak.rotation.z = -Math.PI / 2;
  g.add(beak);
  const outfit = new THREE.MeshStandardMaterial({ color: GOOSE_OUTFITS[0], roughness: 0.6 });
  const coat = mesh(new THREE.CylinderGeometry(0.16, 0.26, 0.28, 12), outfit, 0, 0.26, 0);
  g.add(coat);
  const hat = mesh(new THREE.ConeGeometry(0.1, 0.12, 10), outfit, 0.2, 0.72, 0);
  g.add(hat);
  return {
    group: g,
    setOutfit: (i: number) => outfit.color.set(GOOSE_OUTFITS[i % GOOSE_OUTFITS.length] as string),
  };
}

export function lllSign(): THREE.Group {
  const g = new THREE.Group();
  const tex = signTexture({
    w: 512,
    h: 160,
    bg: '#f3ead8',
    border: '#8a6a45',
    borderWidth: 10,
    lines: [{ text: 'Live Laugh Love', size: 70, font: 'Pacifico', color: '#5b3a29' }],
    grime: true,
  });
  g.add(box(1.5, 0.48, 0.04, mat('#8a6a45'), 0, 0, 0));
  g.add(mesh(new THREE.PlaneGeometry(1.44, 0.44), texMat(tex), 0, 0, 0.022, { cast: false }));
  return g;
}

export function tirePlanter(): THREE.Group {
  const g = new THREE.Group();
  const tire = mesh(new THREE.TorusGeometry(0.42, 0.16, 8, 16), mat('#f4f4f4', { rough: 0.6 }), 0, 0.16, 0);
  tire.rotation.x = Math.PI / 2;
  g.add(tire);
  g.add(cyl(0.4, 0.4, 0.1, mat('#4a3424'), 0, 0.22, 0, 12, { cast: false }));
  const colors = ['#e63946', '#ffd166', '#f78fb3', '#ffffff'];
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const r = i % 3 === 0 ? 0.08 : 0.25;
    g.add(cyl(0.01, 0.01, 0.25, mat('#3f7a2f'), Math.cos(a) * r, 0.35, Math.sin(a) * r, 4));
    g.add(sphere(0.06, mat(colors[i % colors.length] as string), Math.cos(a) * r, 0.5, Math.sin(a) * r, 6));
  }
  return g;
}

export function clothesline(): BuiltDecor {
  const g = new THREE.Group();
  for (const z of [-3, 3]) {
    g.add(cyl(0.05, 0.05, 2.2, M.steel, 0, 1.1, z, 6));
    g.add(box(0.05, 0.05, 0.8, M.steel, 0, 2.15, z).rotateY(Math.PI / 2));
  }
  g.add(box(0.02, 0.02, 6, mat('#dddddd'), 0, 2.1, 0, { cast: false }));
  const clothes = new THREE.Group();
  const items: Array<[string, number, number, number]> = [
    ['#f7f7f2', 0.42, 0.3, -2.2], // tighty-whities
    ['#ff8fb8', 0.5, 0.25, -1.4], // something pink
    ['#5f86b8', 0.6, 0.55, -0.4], // jorts
    ['#c0392b', 0.9, 0.8, 0.8], // XXXL NASCAR tee
    ['#ffffff', 0.15, 0.4, 1.8], // sock
    ['#e8e2c9', 0.15, 0.4, 2.1], // other sock (different)
  ];
  for (const [c, w, h, z] of items) {
    const p = mesh(new THREE.PlaneGeometry(w, h), mat(c, { side: THREE.DoubleSide }), 0, 2.08 - h / 2, z);
    p.rotation.y = Math.PI / 2;
    clothes.add(p);
  }
  g.add(clothes);
  return {
    group: g,
    update: (t) => {
      clothes.children.forEach((c, i) => {
        c.rotation.z = Math.sin(t * 2 + i) * 0.12;
      });
    },
  };
}

export function bigFlag(): BuiltDecor {
  const { group, flag } = flagpole(7, texMat(americanFlagTexture(), { side: THREE.DoubleSide }), 2.6, 1.4);
  return { group, update: (t) => waveFlag(flag, t, 1) };
}

export function christmasLights(length: number): BuiltDecor {
  const g = new THREE.Group();
  g.add(box(length, 0.015, 0.015, M.black, 0, 0, 0, { cast: false }));
  const colors = ['#ff3b3b', '#36d16b', '#3b8bff', '#ffd23b'];
  const mats = colors.map(
    (c) => new THREE.MeshStandardMaterial({ color: c, emissive: new THREE.Color(c), emissiveIntensity: 1 }),
  );
  const geo = new THREE.SphereGeometry(0.05, 6, 4);
  let i = 0;
  for (let x = -length / 2; x <= length / 2; x += 0.45) {
    const sag = Math.abs(Math.sin(((x + length / 2) / 1.8) * Math.PI)) * 0.12;
    const b = new THREE.Mesh(geo, mats[i % mats.length]);
    b.position.set(x, -0.06 - sag, 0);
    g.add(b);
    i++;
  }
  return {
    group: g,
    update: (t, _dt, night) => {
      mats.forEach((m, k) => {
        const blink = Math.sin(t * 2.2 + k * 1.57) > -0.3 ? 1 : 0.15;
        m.emissiveIntensity = (0.25 + night * 2.2) * blink;
      });
    },
  };
}

export function bugZapper(): BuiltDecor {
  const g = new THREE.Group();
  const glow = new THREE.MeshStandardMaterial({
    color: '#7ab8ff',
    emissive: new THREE.Color('#3d7bff'),
    emissiveIntensity: 0,
  });
  g.add(box(0.02, 0.4, 0.02, M.black, 0, 0.35, 0, { cast: false }));
  g.add(box(0.22, 0.3, 0.22, mat('#2a2a2a', { metal: 0.3 }), 0, 0, 0));
  g.add(box(0.16, 0.24, 0.16, glow, 0, 0, 0, { cast: false }));
  return {
    group: g,
    update: (t, _dt, night) => {
      const flash = night > 0.5 && Math.sin(t * 13) > 0.97 ? 3 : 1;
      glow.emissiveIntensity = night * 1.6 * flash;
    },
  };
}

export function satelliteDish(): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.04, 0.04, 0.7, M.steel, 0, 0.35, 0, 6));
  const dish = mesh(
    new THREE.SphereGeometry(0.55, 14, 6, 0, Math.PI * 2, 0, 0.62),
    mat('#e8e8e4', { side: THREE.DoubleSide }),
    0,
    0.85,
    0.15,
  );
  dish.rotation.x = Math.PI / 2 + 0.6;
  g.add(dish);
  const arm = box(0.03, 0.03, 0.55, M.steel, 0, 0.82, 0.45);
  arm.rotation.x = -0.4;
  g.add(arm);
  g.add(box(0.08, 0.08, 0.12, M.black, 0, 0.95, 0.68));
  return g;
}

export function kiddiePool(): BuiltDecor {
  const g = new THREE.Group();
  g.add(cyl(0.95, 0.95, 0.28, mat('#3aa0e8', { side: THREE.DoubleSide }), 0, 0.14, 0, 18, { open: true }));
  const rim = mesh(new THREE.TorusGeometry(0.95, 0.06, 6, 18), mat('#ffffff'), 0, 0.28, 0);
  rim.rotation.x = Math.PI / 2;
  g.add(rim);
  const water = mesh(
    new THREE.CircleGeometry(0.93, 18),
    texMat(waterTexture(), { transparent: true, opacity: 0.85, rough: 0.1 }),
    0,
    0.2,
    0,
    { cast: false },
  );
  water.rotation.x = -Math.PI / 2;
  g.add(water);
  g.add(cyl(0.93, 0.93, 0.02, mat('#3aa0e8'), 0, 0.01, 0, 18, { cast: false }));
  return {
    group: g,
    update: (t) => ((water.material as THREE.MeshStandardMaterial).map!.offset.x = t * 0.02),
  };
}

export function abovegroundPool(): BuiltDecor {
  const g = new THREE.Group();
  const wall = mesh(
    new THREE.CylinderGeometry(2.4, 2.4, 1.25, 28, 1, true),
    texMat(poolWallTexture(), { side: THREE.DoubleSide }),
    0,
    0.625,
    0,
  );
  const uv = wall.geometry.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setX(i, uv.getX(i) * 6);
  g.add(wall);
  const rim = mesh(new THREE.TorusGeometry(2.42, 0.07, 6, 28), mat('#d7dde2'), 0, 1.25, 0);
  rim.rotation.x = Math.PI / 2;
  g.add(rim);
  const waterMat = texMat(waterTexture(), { transparent: true, opacity: 0.88, rough: 0.05 });
  const water = mesh(new THREE.CircleGeometry(2.38, 28), waterMat, 0, 1.08, 0, { cast: false });
  water.rotation.x = -Math.PI / 2;
  g.add(water);
  for (const x of [-0.25, 0.25]) {
    const rail = cyl(0.025, 0.025, 1.8, M.chrome, x, 0.85, 2.65, 6);
    rail.rotation.x = 0.35;
    g.add(rail);
  }
  for (let i = 0; i < 3; i++) g.add(box(0.5, 0.04, 0.12, M.chrome, 0, 0.35 + i * 0.35, 2.75 - i * 0.12));
  const floatie = mesh(new THREE.TorusGeometry(0.35, 0.12, 8, 14), mat('#ffd23b'), 0.8, 1.12, -0.5);
  floatie.rotation.x = Math.PI / 2;
  g.add(floatie);
  return {
    group: g,
    update: (t) => {
      waterMat.map!.offset.set(t * 0.015, t * 0.01);
      floatie.position.x = 0.8 + Math.sin(t * 0.3) * 0.6;
      floatie.position.z = -0.5 + Math.cos(t * 0.23) * 0.6;
    },
  };
}

export function trampoline(): { built: BuiltDecor; mat: THREE.Mesh } {
  const g = new THREE.Group();
  const rust = mat('#7c6e62', { metal: 0.4, rough: 0.7 });
  const frame = mesh(new THREE.TorusGeometry(1.9, 0.05, 6, 28), rust, 0, 0.82, 0);
  frame.rotation.x = Math.PI / 2;
  g.add(frame);
  const pad = mesh(new THREE.TorusGeometry(1.8, 0.16, 4, 28), mat('#2e6ad1'), 0, 0.84, 0);
  pad.rotation.x = Math.PI / 2;
  pad.scale.z = 0.35;
  g.add(pad);
  const surface = mesh(
    new THREE.CircleGeometry(1.62, 24),
    mat('#141414', { side: THREE.DoubleSide }),
    0,
    0.8,
    0,
    { cast: false },
  );
  surface.rotation.x = -Math.PI / 2;
  g.add(surface);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    g.add(cyl(0.035, 0.035, 0.82, rust, Math.cos(a) * 1.85, 0.41, Math.sin(a) * 1.85, 6));
  }
  return { built: { group: g }, mat: surface };
}

export function clawTub(): BuiltDecor {
  const g = new THREE.Group();
  const enamel = mat('#f6f4ee', { rough: 0.3 });
  g.add(box(1.6, 0.08, 0.75, enamel, 0, 0.25, 0));
  g.add(box(1.6, 0.5, 0.06, enamel, 0, 0.5, 0.35));
  g.add(box(1.6, 0.5, 0.06, enamel, 0, 0.5, -0.35));
  g.add(box(0.06, 0.5, 0.75, enamel, 0.78, 0.5, 0));
  g.add(box(0.06, 0.65, 0.75, enamel, -0.78, 0.57, 0));
  const water = mesh(
    new THREE.PlaneGeometry(1.5, 0.65),
    texMat(waterTexture(), { transparent: true, opacity: 0.85 }),
    0,
    0.66,
    0,
    { cast: false },
  );
  water.rotation.x = -Math.PI / 2;
  g.add(water);
  const gold = mat('#c9a43a', { metal: 0.8, rough: 0.3 });
  for (const x of [-0.65, 0.65]) for (const z of [-0.28, 0.28]) g.add(sphere(0.07, gold, x, 0.14, z, 6));
  const hose = mesh(
    new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(-0.7, 0.75, 0),
        new THREE.Vector3(-1.1, 0.4, 0.3),
        new THREE.Vector3(-1.4, 0.03, 0.9),
        new THREE.Vector3(-1.2, 0.03, 1.8),
      ]),
      12,
      0.03,
      5,
    ),
    mat('#2f8f3a'),
  );
  g.add(hose);
  return { group: g };
}

export function inflatableGorilla(): BuiltDecor {
  const g = new THREE.Group();
  const purple = mat('#3b2a5c', { rough: 0.4 });
  const face = mat('#8f7aa8', { rough: 0.4 });
  const body = new THREE.Group();
  g.add(body);
  const torso = sphere(1.0, purple, 0, 1.6, 0, 14);
  torso.scale.set(1.1, 1.25, 0.9);
  body.add(torso);
  body.add(sphere(0.62, purple, 0, 3.05, 0.05, 14));
  const snout = sphere(0.35, face, 0, 2.92, 0.45, 10);
  snout.scale.set(1.2, 0.8, 0.7);
  body.add(snout);
  for (const x of [-0.22, 0.22]) {
    body.add(sphere(0.1, mat('#ffffff'), x, 3.18, 0.5, 8));
    body.add(sphere(0.05, M.black, x, 3.18, 0.58, 6));
  }
  for (const s of [-1, 1]) {
    const arm = cyl(0.28, 0.24, 1.7, purple, s * 1.25, 1.8, 0.2, 10);
    arm.rotation.z = s * 0.55;
    arm.rotation.x = -0.3;
    body.add(arm);
    body.add(cyl(0.32, 0.36, 0.7, purple, s * 0.55, 0.35, 0, 10));
  }
  const sign = mesh(
    new THREE.PlaneGeometry(1.2, 0.6),
    texMat(cardboardTexture('COLD BEER INSIDE')),
    0,
    1.4,
    0.95,
    { cast: false },
  );
  body.add(sign);
  return {
    group: g,
    update: (t) => {
      body.rotation.z = Math.sin(t * 1.3) * 0.04;
      body.rotation.x = Math.sin(t * 0.9) * 0.03;
    },
  };
}

export function tvBox(): THREE.Group {
  const g = new THREE.Group();
  const tex = signTexture({
    w: 512,
    h: 300,
    bg: '#b98d55',
    lines: [
      { text: '75"', size: 110, font: 'Anton', color: '#1b1b1b' },
      { text: 'ULTRA HD SMART TV', size: 44, font: 'Anton', color: '#1b1b1b' },
    ],
    grime: true,
  });
  const b = mesh(new THREE.BoxGeometry(1.9, 1.15, 0.22), texMat(tex), 0, 0.6, 0);
  b.rotation.x = -0.12;
  g.add(b);
  return g;
}

export function possum(): BuiltDecor {
  const g = new THREE.Group();
  const fur = mat('#8d8a86', { rough: 1 });
  const body = new THREE.Group();
  g.add(body);
  const torso = sphere(0.16, fur, 0, 0.15, 0, 10);
  torso.scale.set(1.6, 0.9, 1);
  body.add(torso);
  const head = mesh(new THREE.ConeGeometry(0.1, 0.24, 8), mat('#f1ece4'), 0.32, 0.17, 0);
  head.rotation.z = -Math.PI / 2;
  body.add(head);
  body.add(sphere(0.025, mat('#f2a6b4'), 0.45, 0.17, 0, 6));
  for (const z of [-0.06, 0.06]) body.add(sphere(0.04, M.black, 0.27, 0.27, z, 6));
  const tail = mesh(
    new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(-0.24, 0.12, 0),
        new THREE.Vector3(-0.5, 0.08, 0.1),
        new THREE.Vector3(-0.62, 0.2, 0.2),
      ]),
      8,
      0.022,
      5,
    ),
    mat('#f2b6c0'),
  );
  body.add(tail);
  return {
    group: g,
    update: (t) => {
      // Every so often Kevin plays dead.
      const dead = Math.sin(t * 0.21) > 0.85;
      body.rotation.x = dead ? Math.PI / 2 : Math.sin(t * 3) * 0.03;
      body.position.y = dead ? 0.05 : 0;
    },
  };
}
